import { useMemo, useContext } from "react";
import { NavLink } from "react-router-dom";
import { DateTime } from 'luxon';

import { transformOrderBoxToEnhanced } from '../../hooks/use-logistics-api';
import useLogisticsApi from '../../hooks/use-logistics-api';
import { useCourierRoutesQuery, useWarehouseIncomingQuery, useWarehouseOutgoingQuery } from '../../hooks/use-queries';
import { useQueryBridge } from '../../hooks/use-query-bridge';
import { Order, EnhancedOrderBox, WorkflowType } from '../../types/logistics';
import AuthContext from '../../store/auth-context';
import { cardRoles, hasAccess } from '../../config/roles';
import { AUTO_STORE_ON_RECEIVE } from '../../config/constants';

import './Home.scss';

interface HomeStat {
    name: string;
    progress: number;
    total: number;
}

interface HomeData {
    name: string;
    list: HomeStat[];
}

const Home = () => {
    const authCtx = useContext(AuthContext);
    const userRole = authCtx.user?.role;

    const { calcProgress, applyWorkflowFilter } = useLogisticsApi();

    const showCourier = hasAccess(userRole, cardRoles['courier']);
    const showWarehouse = hasAccess(userRole, cardRoles['incoming']);

    const today = useMemo(() => DateTime.now().toFormat('yyyy-MM-dd'), []);

    // Fetch server data via React Query (cached, deduplicated across pages)
    const { data: courierResult, isLoading: courierLoading, error: courierError } = useCourierRoutesQuery(today, showCourier);
    const { data: incomingOrders, isLoading: incomingLoading, error: incomingError } = useWarehouseIncomingQuery(today, showWarehouse);
    const { data: outgoingResult, isLoading: outgoingLoading, error: outgoingError } = useWarehouseOutgoingQuery(today, showWarehouse);

    const hasData = !!(courierResult || incomingOrders || outgoingResult);
    const loading = (showCourier && courierLoading) || (showWarehouse && (incomingLoading || outgoingLoading));
    const firstError = (courierError || incomingError || outgoingError) as Error | null;

    // Bridge loading/error to global overlay — only shows loading on initial load (no cached data)
    useQueryBridge(loading, firstError, 'Loading dashboard...', 'Failed to load dashboard', hasData);

    // ── Assignment filtering ──────────────────────────────────────────────
    // The backend returns ALL orders for a given date. We filter client-side
    // so each user only sees orders relevant to them.
    //
    // ID relationships:
    //   user.id        = partner_users.id — identifies THIS logged-in user
    //   user.partnerId = partner.id       — the company/org this user belongs to
    //
    // Order assignment fields:
    //   order.assignedPartnerId  — which partner COMPANY handles this order
    //   order.collectionDriverId — which partner_user (driver) does the COLLECTION leg
    //   order.deliveryDriverId   — which partner_user (driver) does the DELIVERY leg
    //
    // Warehouse screens: filter by assignedPartnerId (all warehouse staff in the
    //   same partner org share the same warehouse and see the same orders).
    // Courier screens: filter by collectionDriverId / deliveryDriverId (each
    //   driver only sees their own assigned collection or delivery stops).
    // ────────────────────────────────────────────────────────────────────────
    const myPartnerId = authCtx.user?.partnerId ?? null;
    const myUserId = authCtx.user?.id ?? null;

    // Derive dashboard stats from cached query data
    const data = useMemo<HomeData[]>(() => {
        const results: HomeData[] = [];

        // Warehouse filter: only orders assigned to this user's partner company
        const byPartner = (orders: Order[]) =>
            myPartnerId != null ? orders.filter(o => o.assignedPartnerId === myPartnerId) : orders;

        if (showCourier) {
            // Courier filter: only orders where this specific driver is assigned
            // for collection or delivery (NULL driver = unassigned, excluded)
            const courierOrders = (courierResult?.orders ?? []).filter(o =>
                myUserId != null && (o.collectionDriverId === myUserId || o.deliveryDriverId === myUserId)
            );
            results.push({ name: 'courier', list: calculateCourierStats(courierOrders, applyWorkflowFilter) });
        }
        if (showWarehouse) {
            results.push({ name: 'incoming', list: calculateIncomingStats(byPartner(incomingOrders ?? []), calcProgress) });
            results.push({ name: 'dispatch', list: calculateDispatchStats(byPartner(outgoingResult?.orders ?? []), calcProgress) });
        }

        return results;
    }, [courierResult, incomingOrders, outgoingResult, showCourier, showWarehouse, applyWorkflowFilter, calcProgress, myPartnerId, myUserId]);

    const numbersColour = (numbers: HomeStat) => {
        let classString = '';
        if (numbers.total === 0) return classString;
        if (numbers.progress === numbers.total) classString += ' ok';
        else if (numbers.progress >= numbers.total / 2) classString += ' warn';
        else classString += ' error';
        return classString;
    };

    return (
        <div id="home">
            {data.map((type: HomeData, index: number) => {
                return (
                    <NavLink className={type.name} to={'/' + type.name} key={index}>
                        <h4>{type.name}</h4>
                        <div>
                            {type.list.map((numbers: HomeStat, index: number) => {
                                return (
                                    <div key={index}>
                                        <u>{numbers.name}</u>
                                        <div className={'numbers' + numbersColour(numbers)}>
                                            <span>{numbers.progress}</span>/<span>{numbers.total}</span>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </NavLink>
                );
            })}
        </div>
    );
};

// ─── Stats Calculation (pure functions) ──────────────────────────────────────

function calculateCourierStats(
    orders: Order[],
    applyWorkflowFilter: (boxes: EnhancedOrderBox[], wf: WorkflowType) => EnhancedOrderBox[]
): HomeStat[] {
    let collectionTotal = 0, collectionComplete = 0;
    let deliveryTotal = 0, deliveryComplete = 0;

    orders.forEach((order: Order) => {
        if (!order.storageRequired) return;

        const enhancedBoxes = order.orderBoxes.map(transformOrderBoxToEnhanced);

        if (order.collectionAddress) {
            collectionTotal++;
            if (order.collectionCompletedAt) {
                collectionComplete++;
            } else {
                const boxes = applyWorkflowFilter([...enhancedBoxes], 'collection' as WorkflowType);
                if (boxes.length > 0 && boxes.filter(b => b.isCompleted).length === boxes.length) {
                    collectionComplete++;
                }
            }
        }
        if (order.deliveryAddress) {
            deliveryTotal++;
            if (order.deliveryCompletedAt) {
                deliveryComplete++;
            } else {
                const boxes = applyWorkflowFilter([...enhancedBoxes], 'delivery' as WorkflowType);
                if (boxes.length > 0 && boxes.filter(b => b.isCompleted).length === boxes.length) {
                    deliveryComplete++;
                }
            }
        }
    });

    return [
        { name: 'Collections', progress: collectionComplete, total: collectionTotal },
        { name: 'Deliveries', progress: deliveryComplete, total: deliveryTotal }
    ];
}

function calculateIncomingStats(
    orders: Order[],
    calcProgress: (type: string, data: { boxes?: EnhancedOrderBox[]; type?: string }) => number
): HomeStat[] {
    const allBoxes: EnhancedOrderBox[] = [];
    orders.forEach((order: Order) => {
        allBoxes.push(...order.orderBoxes.map(transformOrderBoxToEnhanced));
    });

    // Use only statusCode for calcProgress — isCompleted is a courier workflow flag
    // (hardcoded false by transformOrderBoxToEnhanced) that short-circuits calcProgress
    const incomingBoxes = allBoxes.filter(b => ['ITKK', 'WKKU', 'WKKS', 'ITTKKFR'].includes(b.statusCode));
    const forProgress = incomingBoxes.map(b => ({ statusCode: b.statusCode }));

    if (AUTO_STORE_ON_RECEIVE) {
        // Merged mode: box-level + order-level progress
        const receivedBoxes = calcProgress('warehouse-store', { boxes: forProgress as any });

        // Order-level: order is "complete" when all its incoming boxes are WKKS
        let completedOrders = 0;
        let totalOrderCount = 0;
        orders.forEach(order => {
            const boxes = order.orderBoxes.map(transformOrderBoxToEnhanced);
            const incoming = boxes.filter(b => ['ITKK', 'WKKU', 'WKKS', 'ITTKKFR'].includes(b.statusCode));
            if (incoming.length === 0) return;
            totalOrderCount++;
            if (incoming.every(b => b.statusCode === 'WKKS')) completedOrders++;
        });

        return [
            { name: 'Boxes', progress: receivedBoxes, total: incomingBoxes.length },
            { name: 'Orders', progress: completedOrders, total: totalOrderCount }
        ];
    }

    return [
        { name: 'Received', progress: calcProgress('warehouse-unload', { boxes: forProgress as any }), total: incomingBoxes.length },
        { name: 'Stored', progress: calcProgress('warehouse-store', { boxes: forProgress as any }), total: incomingBoxes.length }
    ];
}

function calculateDispatchStats(
    orders: Order[],
    calcProgress: (type: string, data: { boxes?: EnhancedOrderBox[]; type?: string }) => number
): HomeStat[] {
    const allBoxes: EnhancedOrderBox[] = [];
    orders.forEach((order: Order) => {
        allBoxes.push(...order.orderBoxes.map(transformOrderBoxToEnhanced));
    });

    const dispatchBoxes = allBoxes.filter(b => ['WKKS', 'WKKDO', 'WKKP', 'WKKHO', 'ITTC'].includes(b.statusCode));
    const forProgress = dispatchBoxes.map(b => ({ statusCode: b.statusCode }));

    // "Ready / Out" = boxes that completed the warehouse pipeline (WKKHO waiting for courier + ITTC on the road)
    const readyOrOut = dispatchBoxes.filter(b => b.statusCode === 'WKKHO' || b.statusCode === 'ITTC').length;

    return [
        { name: 'Picked', progress: calcProgress('warehouse-pick', { boxes: forProgress as any }), total: dispatchBoxes.length },
        { name: 'Ready / Out', progress: readyOrOut, total: dispatchBoxes.length }
    ];
}

export default Home;
